import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
    Building2,
    UserRound,
    Bot,
    Users,
    CreditCard,
    Receipt,
    Pencil,
    Plus,
    KeyRound,
    Trash2,
    Save,
    X,
    Check,
    ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useOnFocus } from "../hooks/useOnFocus";

const Section = ({ icon: Icon, title, children, extra }) => (
    <section className="ap-card p-6 md:p-7 scale-in">
        <div className="flex items-start justify-between gap-4 mb-5">
            <div className="flex items-center gap-3">
                <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: "var(--ap-blue-soft)", color: "var(--ap-blue-2)" }}
                >
                    <Icon size={20} />
                </div>
                <h2 className="ap-section-title">{title}</h2>
            </div>
            {extra}
        </div>
        {children}
    </section>
);

const Field = ({ label, value, mono = false }) => (
    <div className="flex flex-col gap-1.5">
        <span className="ap-label">{label}</span>
        <span
            className={`text-sm md:text-[15px] text-white break-words ${mono ? "font-mono" : ""}`}
            data-testid={`field-${label.replace(/\s+/g, "-").toLowerCase()}`}
        >
            {value || <span className="text-slate-500">—</span>}
        </span>
    </div>
);

const Editable = ({ label, value, onChange, type = "text", locked, hint }) => {
    const tid = `edit-${label.replace(/\s+/g, "-").toLowerCase()}`;
    return (
        <div className="flex flex-col gap-1.5">
            <label className="ap-label flex items-center gap-1.5">
                {label}
                {locked && (
                    <span
                        className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{
                            background: "rgba(148,163,184,0.12)",
                            color: "var(--ap-text-dim)",
                        }}
                    >
                        Locked
                    </span>
                )}
            </label>
            <input
                data-testid={tid}
                type={type}
                disabled={locked}
                readOnly={locked}
                className="ap-input text-sm"
                value={value || ""}
                onChange={(e) => !locked && onChange(e.target.value)}
                style={locked ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
            />
            {hint && (
                <span className="text-[11px]" style={{ color: "var(--ap-text-dim)" }}>
                    {hint}
                </span>
            )}
        </div>
    );
};

const Modal = ({ open, onClose, children, testid }) => {
    if (!open) return null;
    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4"
            onClick={onClose}
            data-testid={testid}
        >
            <div
                className="ap-card p-6 w-full max-w-md scale-in"
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>,
        document.body,
    );
};

export default function ShopDetails({ shop, refresh }) {
    const [editBasic, setEditBasic] = useState(false);
    const [basic, setBasic] = useState(shop?.basic || {});
    const [editAdmin, setEditAdmin] = useState(false);
    const [admin, setAdmin] = useState(shop?.admin || {});
    const [editAgent, setEditAgent] = useState(false);
    const [agent, setAgent] = useState(shop?.agent || {});
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "Staff" });
    const [pwUserId, setPwUserId] = useState(null);
    const [newPw, setNewPw] = useState("");
    const [recentPayments, setRecentPayments] = useState([]);

    useEffect(() => {
        setBasic(shop?.basic || {});
        setAdmin(shop?.admin || {});
        setAgent(shop?.agent || {});
    }, [shop]);

    useEffect(() => {
        if (!shop?.id) return;
        api.payments({ limit: 5 })
            .then((d) => setRecentPayments((d.payments || []).slice(0, 5)))
            .catch(() => {
                /* silent */
            });
    }, [shop?.id]);

    // Silent refresh on focus — pulses the balance when it moves and surfaces
    // a toast if a new payment just landed (e.g. completed on another device).
    const [balancePulse, setBalancePulse] = useState(false);
    const knownPaymentIdsRef = useRef(new Set(recentPayments.map((p) => p.id)));
    useEffect(() => {
        knownPaymentIdsRef.current = new Set(recentPayments.map((p) => p.id));
    }, [recentPayments]);

    useOnFocus(async () => {
        if (!shop?.id) return;
        try {
            const [fresh, txs] = await Promise.all([
                api.me().catch(() => null),
                api.payments({ limit: 5 }).catch(() => ({ payments: [] })),
            ]);
            const list = (txs?.payments || []).slice(0, 5);
            const newOnes = list.filter((p) => !knownPaymentIdsRef.current.has(p.id));
            setRecentPayments(list);
            // Compare balance and pulse if it moved up
            const oldTotal =
                (shop.credits?.subscription_credits || 0) +
                (shop.credits?.recharge_credits || 0);
            const newTotal =
                (fresh?.credits?.subscription_credits || 0) +
                (fresh?.credits?.recharge_credits || 0);
            if (newTotal > oldTotal || newOnes.length > 0) {
                if (fresh) refresh?.();
                setBalancePulse(true);
                setTimeout(() => setBalancePulse(false), 3000);
                if (newOnes.length > 0) {
                    toast.success(
                        newOnes.length === 1
                            ? `New payment: ${newOnes[0].description}`
                            : `${newOnes.length} new payments landed`,
                    );
                }
            }
        } catch {
            /* silent */
        }
    });

    const saveBasic = async () => {
        if (!shop?.shopId) {
            toast.error("Missing shop id");
            return;
        }
        try {
            await api.updateShopSection(shop.shopId, "shopBasicInfo", {
                shopName: basic.name,
                legalBusinessName: basic.business_name,
                shopPhoneNumber: basic.phone,
                shopEmail: basic.email,
                websiteLink: basic.website,
                address: basic.address,
                city: basic.city,
                state: basic.state,
                zipCode: basic.zip,
                shopTimezone: basic.timezone,
            });
            toast.success("Basic info updated");
            setEditBasic(false);
            refresh();
        } catch (e) {
            toast.error(e?.message || "Update failed");
        }
    };
    const saveAdmin = async () => {
        try {
            await api.updateAdmin(admin);
            toast.success("Admin info updated");
            setEditAdmin(false);
            refresh();
        } catch (e) {
            toast.error("Update failed");
        }
    };
    const saveAgent = async () => {
        try {
            await api.updateAgent({
                ...agent,
                languages: Array.isArray(agent.languages)
                    ? agent.languages
                    : String(agent.languages || "")
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
            });
            toast.success("Agent details updated");
            setEditAgent(false);
            refresh();
        } catch (e) {
            toast.error("Update failed");
        }
    };

    const createUser = async () => {
        if (!newUser.name || !newUser.email || !newUser.password) {
            toast.error("All fields are required");
            return;
        }
        try {
            await api.createUser(newUser);
            toast.success("User created");
            setShowCreateUser(false);
            setNewUser({ name: "", email: "", password: "", role: "Staff" });
            refresh();
        } catch (e) {
            toast.error("Failed to create user");
        }
    };

    const changePassword = async () => {
        if (!newPw || newPw.length < 4) {
            toast.error("Password too short");
            return;
        }
        try {
            await api.changePassword(pwUserId, newPw);
            toast.success("Password updated");
            setPwUserId(null);
            setNewPw("");
        } catch (e) {
            toast.error("Failed to update password");
        }
    };

    const deleteUser = async (id) => {
        if (!confirm("Remove this user?")) return;
        try {
            await api.deleteUser(id);
            toast.success("User removed");
            refresh();
        } catch (e) {
            toast.error("Failed to remove user");
        }
    };

    if (!shop) return null;
    const sub = shop.subscription || {};
    const credits = shop.credits || {};

    return (
        <div className="flex flex-col gap-6 fade-in" data-testid="shop-details-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div
                        className="ap-label mb-1"
                        style={{ color: "var(--ap-blue-2)" }}
                        data-testid="page-tagline"
                    >
                        Auto Shop Owner / Manager
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                        Shop Details
                    </h1>
                    <p className="text-sm mt-1" style={{ color: "var(--ap-text-dim)" }}>
                        Review and manage everything about your shop in one place.
                    </p>
                </div>
            </div>

            {/* a) Basic Info */}
            <Section
                icon={Building2}
                title="Basic Info"
                extra={
                    !editBasic ? (
                        <button
                            data-testid="edit-basic-btn"
                            onClick={() => setEditBasic(true)}
                            className="ap-btn-ghost text-xs flex items-center gap-2"
                        >
                            <Pencil size={14} /> Edit
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                data-testid="cancel-basic-btn"
                                onClick={() => {
                                    setEditBasic(false);
                                    setBasic(shop.basic);
                                }}
                                className="ap-btn-ghost text-xs flex items-center gap-2"
                            >
                                <X size={14} /> Cancel
                            </button>
                            <button
                                data-testid="save-basic-btn"
                                onClick={saveBasic}
                                className="ap-btn-primary text-xs flex items-center gap-2 py-2"
                            >
                                <Save size={14} /> Save
                            </button>
                        </div>
                    )
                }
            >
                {!editBasic ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                        <Field label="Shop Name" value={basic.name} />
                        <Field label="Business Name" value={basic.business_name} />
                        <Field label="Address" value={basic.address} />
                        <Field
                            label="City / State / Zip"
                            value={`${basic.city || ""}, ${basic.state || ""} ${basic.zip || ""}`}
                        />
                        <Field label="Phone" value={basic.phone} />
                        <Field label="Email" value={basic.email} />
                        <Field label="Website" value={basic.website} />
                        <Field label="Timezone" value={basic.timezone} />
                        <Field label="Number of Bays" value={String(basic.bays || "")} />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <Editable label="Shop Name" value={basic.name} onChange={(v) => setBasic({ ...basic, name: v })} />
                        <Editable label="Business Name" value={basic.business_name} onChange={(v) => setBasic({ ...basic, business_name: v })} />
                        <Editable label="Address" value={basic.address} onChange={(v) => setBasic({ ...basic, address: v })} />
                        <Editable label="City" value={basic.city} onChange={(v) => setBasic({ ...basic, city: v })} />
                        <Editable label="State" value={basic.state} onChange={(v) => setBasic({ ...basic, state: v })} />
                        <Editable label="Zip" value={basic.zip} onChange={(v) => setBasic({ ...basic, zip: v })} />
                        <Editable label="Phone" value={basic.phone} onChange={(v) => setBasic({ ...basic, phone: v })} />
                        <Editable label="Email" value={basic.email} onChange={(v) => setBasic({ ...basic, email: v })} />
                        <Editable label="Website" value={basic.website} onChange={(v) => setBasic({ ...basic, website: v })} />
                        <Editable label="Timezone" value={basic.timezone} onChange={(v) => setBasic({ ...basic, timezone: v })} />
                        <Editable label="Number of Bays" type="number" value={basic.bays} onChange={(v) => setBasic({ ...basic, bays: v })} locked hint="Contact AutoPilot support to change this" />
                    </div>
                )}
            </Section>

            {/* b) Admin Info */}
            <Section
                icon={UserRound}
                title="Admin Info"
                extra={
                    !editAdmin ? (
                        <button
                            data-testid="edit-admin-btn"
                            onClick={() => setEditAdmin(true)}
                            className="ap-btn-ghost text-xs flex items-center gap-2"
                        >
                            <Pencil size={14} /> Edit
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setEditAdmin(false);
                                    setAdmin(shop.admin);
                                }}
                                className="ap-btn-ghost text-xs flex items-center gap-2"
                            >
                                <X size={14} /> Cancel
                            </button>
                            <button
                                data-testid="save-admin-btn"
                                onClick={saveAdmin}
                                className="ap-btn-primary text-xs flex items-center gap-2 py-2"
                            >
                                <Save size={14} /> Save
                            </button>
                        </div>
                    )
                }
            >
                {!editAdmin ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                        <Field label="Admin Name" value={admin.name} />
                        <Field label="Admin Email" value={admin.email} />
                        <Field label="Admin Phone" value={admin.phone} />
                        <Field label="Role" value={admin.role} />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <Editable label="Admin Name" value={admin.name} onChange={(v) => setAdmin({ ...admin, name: v })} />
                        <Editable label="Admin Email" value={admin.email} onChange={(v) => setAdmin({ ...admin, email: v })} />
                        <Editable label="Admin Phone" value={admin.phone} onChange={(v) => setAdmin({ ...admin, phone: v })} />
                        <Editable label="Role" value={admin.role} onChange={(v) => setAdmin({ ...admin, role: v })} />
                    </div>
                )}
            </Section>

            {/* c) Shop Agent details */}
            <Section
                icon={Bot}
                title="Shop Agent Details"
                extra={
                    !editAgent ? (
                        <button
                            data-testid="edit-agent-btn"
                            onClick={() => setEditAgent(true)}
                            className="ap-btn-ghost text-xs flex items-center gap-2"
                        >
                            <Pencil size={14} /> Edit
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setEditAgent(false);
                                    setAgent(shop.agent);
                                }}
                                className="ap-btn-ghost text-xs flex items-center gap-2"
                            >
                                <X size={14} /> Cancel
                            </button>
                            <button
                                data-testid="save-agent-btn"
                                onClick={saveAgent}
                                className="ap-btn-primary text-xs flex items-center gap-2 py-2"
                            >
                                <Save size={14} /> Save
                            </button>
                        </div>
                    )
                }
            >
                {!editAgent ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                        <Field label="Agent Name" value={agent.agent_name} />
                        <Field label="Agent Phone" value={agent.agent_phone} mono />
                        <Field label="Voice" value={agent.voice} />
                        <Field label="Forwarding Number" value={agent.forwarding_number} mono />
                        <Field label="Business Hours" value={agent.business_hours} />
                        <Field
                            label="Languages"
                            value={(agent.languages || []).join(", ")}
                        />
                        <div className="md:col-span-2">
                            <Field label="Greeting Script" value={agent.greeting} />
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <Editable label="Agent Name" value={agent.agent_name} onChange={(v) => setAgent({ ...agent, agent_name: v })} />
                        <Editable label="Agent Phone" value={agent.agent_phone} onChange={(v) => setAgent({ ...agent, agent_phone: v })} locked hint="Managed by AutoPilot — cannot be changed" />
                        <Editable label="Voice" value={agent.voice} onChange={(v) => setAgent({ ...agent, voice: v })} />
                        <Editable label="Forwarding Number" value={agent.forwarding_number} onChange={(v) => setAgent({ ...agent, forwarding_number: v })} />
                        <Editable label="Business Hours" value={agent.business_hours} onChange={(v) => setAgent({ ...agent, business_hours: v })} />
                        <Editable
                            label="Languages (comma separated)"
                            value={Array.isArray(agent.languages) ? agent.languages.join(", ") : agent.languages}
                            onChange={(v) => setAgent({ ...agent, languages: v })}
                        />
                        <div className="md:col-span-2">
                            <label className="ap-label block mb-2">Greeting Script</label>
                            <textarea
                                className="ap-input min-h-[90px]"
                                value={agent.greeting || ""}
                                onChange={(e) => setAgent({ ...agent, greeting: e.target.value })}
                            />
                        </div>
                    </div>
                )}
            </Section>

            {/* d) Shop Users */}
            <Section
                icon={Users}
                title="Shop Users"
                extra={
                    <button
                        data-testid="add-user-btn"
                        onClick={() => setShowCreateUser(true)}
                        className="ap-btn-primary text-xs flex items-center gap-2 py-2"
                    >
                        <Plus size={14} /> Add user
                    </button>
                }
            >
                <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-sm" data-testid="users-table">
                        <thead>
                            <tr className="text-left" style={{ color: "var(--ap-text-dim)" }}>
                                <th className="px-3 py-2 font-medium">Name</th>
                                <th className="px-3 py-2 font-medium">Email</th>
                                <th className="px-3 py-2 font-medium">Role</th>
                                <th className="px-3 py-2 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(shop.users || []).map((u) => (
                                <tr
                                    key={u.id}
                                    className="border-t"
                                    style={{ borderColor: "var(--ap-line)" }}
                                    data-testid={`user-row-${u.id}`}
                                >
                                    <td className="px-3 py-3 font-medium text-white">{u.name}</td>
                                    <td className="px-3 py-3 text-slate-300">{u.email}</td>
                                    <td className="px-3 py-3">
                                        <span
                                            className="px-2 py-1 rounded-md text-xs font-medium"
                                            style={{
                                                background: "var(--ap-blue-soft)",
                                                color: "var(--ap-blue-2)",
                                            }}
                                        >
                                            {u.role}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                data-testid={`change-pw-btn-${u.id}`}
                                                onClick={() => {
                                                    setPwUserId(u.id);
                                                    setNewPw("");
                                                }}
                                                className="ap-btn-ghost text-xs flex items-center gap-1.5 py-1.5 px-3"
                                            >
                                                <KeyRound size={13} /> Change password
                                            </button>
                                            {u.role !== "Owner" && (
                                                <button
                                                    data-testid={`delete-user-btn-${u.id}`}
                                                    onClick={() => deleteUser(u.id)}
                                                    className="ap-btn-danger text-xs flex items-center gap-1.5 py-1.5 px-3"
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Modal
                    open={showCreateUser}
                    onClose={() => setShowCreateUser(false)}
                    testid="create-user-modal"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold">Create user</h3>
                        <button
                            onClick={() => setShowCreateUser(false)}
                            className="text-slate-400 hover:text-white"
                        >
                            <X size={18} />
                        </button>
                    </div>
                    <div className="flex flex-col gap-3">
                        <Editable label="Name" value={newUser.name} onChange={(v) => setNewUser({ ...newUser, name: v })} />
                        <Editable label="Email" value={newUser.email} onChange={(v) => setNewUser({ ...newUser, email: v })} />
                        <Editable label="Password" type="password" value={newUser.password} onChange={(v) => setNewUser({ ...newUser, password: v })} />
                        <Editable label="Role" value={newUser.role} onChange={(v) => setNewUser({ ...newUser, role: v })} />
                        <button
                            data-testid="create-user-submit"
                            onClick={createUser}
                            className="ap-btn-primary mt-2 flex items-center justify-center gap-2"
                        >
                            <Check size={16} /> Create user
                        </button>
                    </div>
                </Modal>

                <Modal
                    open={!!pwUserId}
                    onClose={() => setPwUserId(null)}
                    testid="change-password-modal"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold">Change password</h3>
                        <button
                            onClick={() => setPwUserId(null)}
                            className="text-slate-400 hover:text-white"
                        >
                            <X size={18} />
                        </button>
                    </div>
                    <Editable
                        label="New password"
                        type="password"
                        value={newPw}
                        onChange={setNewPw}
                    />
                    <button
                        data-testid="submit-change-password"
                        onClick={changePassword}
                        className="ap-btn-primary mt-4 w-full flex items-center justify-center gap-2"
                    >
                        <KeyRound size={16} /> Update password
                    </button>
                </Modal>
            </Section>

            {/* e) Subscription */}
            <Section
                icon={CreditCard}
                title="Subscription"
                extra={
                    <Link
                        to="/shop/subscription"
                        data-testid="manage-subscription-btn"
                        className="ap-btn-ghost text-xs flex items-center gap-2"
                    >
                        Manage <ChevronRight size={14} />
                    </Link>
                }
            >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="ap-card-soft p-5">
                        <div className="ap-label mb-1">Current plan</div>
                        <div className="text-2xl font-bold text-white">
                            {sub.plan_name || "No plan"}
                        </div>
                        <div className="text-xs mt-1" style={{ color: "var(--ap-text-dim)" }}>
                            Status:{" "}
                            <span
                                style={{
                                    color:
                                        sub.status === "active"
                                            ? "var(--ap-green)"
                                            : sub.status === "cancelled"
                                                ? "var(--ap-orange)"
                                                : "var(--ap-text-dim)",
                                }}
                            >
                                {sub.status || "—"}
                            </span>
                        </div>
                    </div>
                    <div className="ap-card-soft p-5">
                        <div className="ap-label mb-1">Cycle ends</div>
                        <div className="text-2xl font-bold text-white">
                            {sub.cycle_end ? new Date(sub.cycle_end).toLocaleDateString() : "—"}
                        </div>
                        {sub.cancels_at_cycle_end && (
                            <div className="text-xs mt-1" style={{ color: "var(--ap-orange)" }}>
                                Cancels at cycle end
                            </div>
                        )}
                    </div>
                    <div
                        className={`ap-card-soft p-5 ${balancePulse ? "ap-pulse" : ""}`}
                        data-testid="total-balance-card"
                    >
                        <div className="ap-label mb-1">Total balance</div>
                        <div className="text-2xl font-bold text-white">
                            {(
                                (credits.subscription_credits || 0) + (credits.recharge_credits || 0)
                            ).toLocaleString()}
                        </div>
                        <div className="text-xs mt-1" style={{ color: "var(--ap-text-dim)" }}>
                            <span style={{ color: "var(--ap-blue-2)" }}>
                                {credits.subscription_credits?.toLocaleString() || 0}
                            </span>{" "}
                            subscription ·{" "}
                            <span style={{ color: "var(--ap-green)" }}>
                                {credits.recharge_credits?.toLocaleString() || 0}
                            </span>{" "}
                            recharge
                        </div>
                    </div>
                </div>
            </Section>

            {/* f) Payment History (snippet) */}
            <Section
                icon={Receipt}
                title="Payment History"
                extra={
                    <Link
                        to="/shop/payments"
                        data-testid="view-all-payments-btn"
                        className="ap-btn-ghost text-xs flex items-center gap-2"
                    >
                        View all <ChevronRight size={14} />
                    </Link>
                }
            >
                <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-sm" data-testid="payments-snippet-table">
                        <thead>
                            <tr className="text-left" style={{ color: "var(--ap-text-dim)" }}>
                                <th className="px-3 py-2 font-medium">Date</th>
                                <th className="px-3 py-2 font-medium">Description</th>
                                <th className="px-3 py-2 font-medium">Type</th>
                                <th className="px-3 py-2 font-medium text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentPayments.map((p) => (
                                <tr key={p.id} className="border-t" style={{ borderColor: "var(--ap-line)" }}>
                                    <td className="px-3 py-3 text-slate-300">
                                        {new Date(p.date).toLocaleDateString()}
                                    </td>
                                    <td className="px-3 py-3 text-white">{p.description}</td>
                                    <td className="px-3 py-3 capitalize text-slate-400">
                                        {p.type.replace("_", " ")}
                                    </td>
                                    <td className="px-3 py-3 text-right font-semibold text-white">
                                        ${p.amount.toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                            {recentPayments.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="text-center py-6 text-slate-500">
                                        No payments yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Section>
        </div>
    );
}
